import api from '@/lib/api/axios'

/**
 * The Hierarchy Advisor's REST surface, coded to docs/hierarchy-advisor-contract.md (29 Sep 2026).
 *
 * Every response is the app's envelope, `{ success, data }`. The reads and the advisor's runs need
 * `hierarchy.review`; the customer's switches and exclusions need `hierarchy.configure`. Built in
 * parallel with the backend, so the shapes here are the contract's, not a server's.
 */

export type Severity = 'high' | 'medium' | 'low'
export const SEVERITIES: readonly Severity[] = ['high', 'medium', 'low']

/**
 * The four levels, in Andrew's order of importance (29 Sep 2026): a missing group (no
 * authentication under a service) matters more than a missing object, which matters more than a
 * misconfigured one, which matters more than a gap in the data. The panel renders them in this order.
 */
export type LevelKey = 'missingGroups' | 'missingObjects' | 'misconfigured' | 'dataGaps'
export const LEVELS: readonly LevelKey[] = ['missingGroups', 'missingObjects', 'misconfigured', 'dataGaps']

export interface ObjectRef {
    id: string
    name: string
    classification?: string
    /** How many objects are beneath it in the scope (services and misconfigured objects). */
    beneath?: number
}

export interface MissingGroupFinding {
    ruleId: string
    capability: { key: string; name: string }
    severity: Severity
    count: number
    services: ObjectRef[]
}

export interface LinkRef {
    mappingId: number
    provider: ObjectRef
    dependent: ObjectRef
}

/** A rule's finding at the misconfigured and data-gap levels: objects, or (rank_violation) links. */
export interface RuleFinding {
    ruleId: string
    severity: Severity
    count: number
    objects?: ObjectRef[]
    links?: LinkRef[]
}

export type AdviceStatus = 'open' | 'accepted' | 'dismissed'

/**
 * Where the missing object goes: `beneath` the anchor (the anchor depends on it - the usual case)
 * or `above` it (it sits on top of the anchor and depends on it: an add-on, such as an Active
 * Directory data backup above its domain controller).
 */
export type Position = 'beneath' | 'above'

/**
 * An object already in the estate that a suggestion could be linked to instead of creating one
 * (Andrew, 1 Oct 2026: "the object might already exist but has not been added to the hierarchy").
 */
export interface Candidate {
    id: string
    name: string
    classification: string
    /** Its site's name, when it has one. */
    location?: string
    /** Already somewhere in the reviewed hierarchy, though not linked to this anchor. */
    inHierarchy: boolean
    /** Why it is offered where it is: the advisor picked it, its name says so, it is at the anchor's site, or none of those. */
    match: 'advisor' | 'name' | 'site' | 'none'
}

/** One of the advisor agent's suggestions: an object that looks missing beneath, or on top of, `anchor`. */
export interface Suggestion {
    id: number
    anchor: { id: string; name: string }
    title: string
    expected: { classificationKey: string; classificationName: string; role: string }
    position: Position
    evidence: string
    reasoning: string
    /** The advisor's own judgement of itself - never a verdict. */
    confidence: Severity
    status: AdviceStatus
    reviewId: number
    /**
     * The existing objects that FIT - the ones the advisor picked as being, or able to serve as, the
     * missing object (or, for a suggestion it made no picks for, those whose name says so). At most 8.
     */
    candidates: Candidate[]
    /** How many fit in all. */
    candidateCount: number
    /**
     * The rest of the expected classification: not a recommendation, kept so a person can still link
     * one (Andrew, 2 Oct 2026: a missing Entra ID tenant listed every authentication server). At most 8.
     */
    otherCandidates: Candidate[]
    /** How many others there are in all. */
    otherCount: number
}

/** POST /advice/:id/apply: link an existing object, or create one and link it. */
export type ApplyRequest =
    | { mode: 'link'; objectId: string }
    | { mode: 'create'; objectId: string; description?: string }

/** What applying a suggestion did (the same answer if it is asked again). */
export interface ApplyResult {
    adviceId: number
    status: AdviceStatus
    position: Position
    anchorId: string
    applied: { objectId: string; created: boolean; by: number | null; at: string }
    /** The link that now exists: `providerId` feeds `dependentId`, in `scenario`. */
    link: { scenario: number; providerId: string; dependentId: string }
    alreadyApplied: boolean
}

export type ReviewStatus = 'running' | 'done' | 'failed'

export interface Review {
    id: number
    root?: string
    scenario?: number
    status: ReviewStatus
    startedAt?: string | null
    finishedAt?: string | null
    suggestionCount?: number
    error?: string | null
    model?: string | null
}

export interface Findings {
    root: { id: string; name: string }
    scenario: number
    objectCount: number
    levels: {
        missingGroups: MissingGroupFinding[]
        missingObjects: Suggestion[]
        misconfigured: RuleFinding[]
        dataGaps: RuleFinding[]
    }
    /** Accepted suggestions nothing has been done about yet: still to be linked or created. */
    acceptedNotApplied: Suggestion[]
    /** Per object in scope: the highest severity and how many findings name it. The canvas badges. */
    byObject: Record<string, { severity: Severity; count: number }>
    /** How much the customer's switches and exclusions hide, so a clean panel is an honest one. */
    hidden: { disabledRules: number; excluded: number }
    review: Review | null
}

export interface Rule {
    ruleId: string
    level: LevelKey
    defaultSeverity: Severity
    enabled: boolean
    severity: Severity | null
    /** Capabilities only: the capability's Name. */
    name?: string
}

export interface Exclusion {
    id: number
    ruleId: string
    object: { id: string; name: string }
    reason: string
    createdBy: string | number | null
    createdAt: string
}

/** The React Query keys the contract names. Scenario is a string so '1' and 1 share one entry. */
export const hierarchyKeys = {
    findingsAll: ['hierarchy-findings'] as const,
    findings: (root: string, scenario: string | number) => ['hierarchy-findings', root, String(scenario)] as const,
    rules: ['hierarchy-rules'] as const,
    exclusionsAll: ['hierarchy-exclusions'] as const,
    exclusions: (root: string) => ['hierarchy-exclusions', root] as const,
    /** The newest review of a root and scenario: the one the panel shows, and polls while it runs. */
    latestReview: (root: string, scenario: string | number) => ['hierarchy-review-latest', root, String(scenario)] as const,
}

const BASE = '/api/hierarchy-advisor'

const unwrap = <T,>(res: { data: unknown }): T => {
    const body = res.data as { data?: T } | T
    return ((body as { data?: T })?.data ?? body) as T
}

export const httpStatusOf = (error: unknown): number | undefined =>
    (error as { response?: { status?: number } } | null)?.response?.status

/**
 * The running review a 409 from POST /reviews names ("one running review per root and scenario;
 * a second POST answers 409 with that review's id"). The contract fixes the id, not where in the
 * body it sits, so the envelope's data, the body itself and a `reviewId` are all read.
 */
export function runningReviewIdOf(error: unknown): number | null {
    const body = (error as { response?: { data?: unknown } } | null)?.response?.data as
        | { data?: { id?: unknown }; id?: unknown; reviewId?: unknown }
        | undefined
    const id = Number(body?.data?.id ?? body?.id ?? body?.reviewId)
    return Number.isFinite(id) && id > 0 ? id : null
}

export const hierarchyAdvisorApi = {
    findings: (root: string, scenario: string | number) =>
        api.get(`${BASE}/findings`, { params: { root, scenario } }).then(unwrap<Findings>),

    rules: () => api.get(`${BASE}/rules`).then(unwrap<Rule[]>),

    putRule: (ruleId: string, body: { enabled: boolean; severity: Severity | null }) =>
        api.put(`${BASE}/rules/${encodeURIComponent(ruleId)}`, body).then(unwrap<Rule>),

    exclusions: (root: string) =>
        api.get(`${BASE}/exclusions`, { params: { root } }).then(unwrap<Exclusion[]>),

    exclude: (body: { ruleId: string; objectId: string; reason: string }) =>
        api.post(`${BASE}/exclusions`, body).then(unwrap<Exclusion>),

    removeExclusion: (id: number) => api.delete(`${BASE}/exclusions/${id}`).then(() => undefined),

    startReview: (root: string, scenario: string | number) =>
        api.post(`${BASE}/reviews`, { root, scenario: Number(scenario) }).then(unwrap<{ id: number; status: ReviewStatus }>),

    review: (id: number) => api.get(`${BASE}/reviews/${id}`).then(unwrap<Review>),

    // Not unwrap: "no review yet" is `data: null`, which unwrap would read as no envelope at all
    // and hand back the envelope itself.
    latestReview: (root: string, scenario: string | number) =>
        api
            .get(`${BASE}/reviews/latest`, { params: { root, scenario } })
            .then((res) => (res.data as { data?: Review | null } | null)?.data ?? null),

    /** Record a decision without acting on it (and Dismiss). Accepting AND doing the work is `apply`. */
    decide: (adviceId: number, body: { decision: 'accepted' | 'dismissed'; reason?: string }) =>
        api.post(`${BASE}/advice/${adviceId}/decision`, body).then(unwrap<Suggestion>),

    apply: (adviceId: number, body: ApplyRequest) =>
        api.post(`${BASE}/advice/${adviceId}/apply`, body).then(unwrap<ApplyResult>),
}
